import { listAvailableLotsForDispenseApi, listRecordableLotsForProductApi } from 'api/inventory';
import { useCallback, useEffect, useState } from 'react';

export const EMPTY_FACILITY_LOTS = {
  ndcOptions: [],
  depletedLots: [],
  availableLots: [],
  // True once listAvailableLotsForDispense has answered for this product + pharmacy. Until
  // then — and after a failed fetch — callers must NOT treat "not in availableLots" as "not
  // in stock here", and fall back to the scan-app listing.
  availableLoaded: false,
  hasInventoryMaster: true,
  loading: false,
  notice: null,
};

/** The Rx's product as inventory knows it AT THIS PHARMACY, fetched once per
 *  (product, pharmacy) while a session is open:
 *   - `availableLots`: dispensable stock here — remaining_qty > 0, not expired
 *     (listAvailableLotsForDispense, the Fill Product picker's own source);
 *   - `depletedLots`: rows here at remaining_qty <= 0 (listRecordableLotsForProduct,
 *     POM-6083), i.e. the unverified-lot candidates;
 *   - `ndcOptions`: the product's closed NDC list.
 *
 *  Why this exists at all: the scan app verifies a read against GET /inventory,
 *  which has NO pharmacy dimension and reports depleted lots too (quantity 0,
 *  dropped on the way to the CSV). So a lot stocked only at ANOTHER pharmacy,
 *  or drained at this one, verifies exactly like a lot on this shelf — and is
 *  then refused at commit (INVENTORY_LOT_NOT_FOUND). This is the facility-scoped
 *  truth both the vial scan path and AddDrugByLotDialog check against instead.
 *
 *  Both fetches are independent: a failed available-lots fetch leaves
 *  `availableLoaded` false so scanning degrades to the listing-based behaviour;
 *  a failed recordable fetch leaves `depletedLots` empty with a notice, so
 *  scanned lots take the ordinary path and the dialog says why it cannot record
 *  by hand.
 *
 *  `reload()` refetches for the same product + pharmacy — AddDrugByLotDialog
 *  calls it on open, so a receipt landed since the session began is offered. */
const useFacilityLots = ({ enabled, productId, fulfillingPharmacyId }) => {
  const [state, setState] = useState(EMPTY_FACILITY_LOTS);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    if (!enabled || !productId || !fulfillingPharmacyId) {
      setState(EMPTY_FACILITY_LOTS);
      return undefined;
    }
    let cancelled = false;
    // A reload for the SAME product + pharmacy keeps the current lists while it runs, so the
    // scan gate does not briefly degrade; a different product starts from empty.
    const key = `${productId}:${fulfillingPharmacyId}`;
    setState((prev) =>
      prev.key === key ? { ...prev, loading: true } : { ...EMPTY_FACILITY_LOTS, key, loading: true }
    );

    const recordable = listRecordableLotsForProductApi({ productId, fulfillingPharmacyId }).then(
      (data) => ({
        ndcOptions: data.ndcOptions ?? [],
        depletedLots: data.depletedLots ?? [],
        hasInventoryMaster: data.hasInventoryMaster !== false,
        notice:
          data.hasInventoryMaster === false
            ? 'Inventory has no NDC on record for this product, so a lot cannot be recorded by hand. Ask an inventory user to add the product.'
            : null,
      }),
      () => ({
        ndcOptions: [],
        depletedLots: [],
        hasInventoryMaster: true,
        notice:
          'Could not load the NDCs for this product, so a lot cannot be recorded by hand. Re-scan the Rx to retry.',
      })
    );

    const available = listAvailableLotsForDispenseApi({ productId, fulfillingPharmacyId }).then(
      (data) => ({ availableLots: data.items ?? [], availableLoaded: true }),
      () => ({ availableLots: [], availableLoaded: false })
    );

    Promise.all([recordable, available]).then(([rec, avail]) => {
      if (cancelled) return;
      setState({ ...rec, ...avail, key, loading: false });
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, productId, fulfillingPharmacyId, reloadKey]);

  return { ...state, reload };
};

export default useFacilityLots;
